import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/globals.css";
import { Link, useParams } from "react-router-dom";

import { listClips } from "../api/media";
import type { MediaClip } from "../api/media";
import { getProject, saveTimeline } from "../api/projects";
import MediaLibrary from "../components/editor/MediaLibrary";
import Preview from "../components/editor/Preview";
import Timeline from "../components/editor/Timeline";
import Inspector from "../components/editor/Inspector";
import TransitionsPanel from "../components/editor/TransitionsPanel";
import EffectsPanel from "../components/editor/EffectsPanel";
import MusicPanel from "../components/editor/MusicPanel";
import WelcomeTour, { shouldShowTour } from "../components/editor/WelcomeTour";
import { useEditor } from "../store/editor";

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export default function EditorPage() {
  const { projectId = "0" } = useParams();
  const pid = Number(projectId);
  const setProject = useEditor((s) => s.setProject);
  const timeline = useEditor((s) => s.timeline);
  const duration = useEditor((s) => s.durationMs());
  const dirty = useEditor((s) => s.dirty);
  const markSaved = useEditor((s) => s.markSaved);
  const saveTimer = useRef<number | null>(null);
  const [transitionsOpen, setTransitionsOpen] = useState(false);
  const [effectsOpen, setEffectsOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  // Autoshow tour la primera vez
  useEffect(() => {
    const t = setTimeout(() => {
      if (shouldShowTour()) setTourOpen(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  function openPanel(which: "transitions" | "effects" | "music") {
    // Solo un panel abierto a la vez (todos comparten posición izquierda)
    setTransitionsOpen(which === "transitions");
    setEffectsOpen(which === "effects");
    setMusicOpen(which === "music");
  }
  function closeAll() {
    setTransitionsOpen(false);
    setEffectsOpen(false);
    setMusicOpen(false);
  }

  // Mediateca compartida: un solo fetch + refresh cada 4s, pasado a hijos
  const [library, setLibrary] = useState<MediaClip[]>([]);
  const refreshLibrary = useCallback(async () => {
    try { setLibrary(await listClips(pid)); }
    catch (e) { console.error("refresh library:", e); }
  }, [pid]);
  useEffect(() => {
    refreshLibrary();
    const t = setInterval(refreshLibrary, 4000);
    return () => clearInterval(t);
  }, [refreshLibrary]);
  const mediaByClipId = useMemo(() => {
    const m: Record<number, MediaClip> = {};
    for (const c of library) m[c.id] = c;
    return m;
  }, [library]);

  useEffect(() => {
    getProject(pid).then((p) => setProject(p.id, p.timeline_json!));
  }, [pid, setProject]);

  useEffect(() => {
    if (!dirty) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        await saveTimeline(pid, timeline, duration);
        markSaved();
      } catch (e) {
        console.error("save failed", e);
      }
    }, 1200);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [timeline, dirty, duration, pid, markSaved]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      const s = useEditor.getState();
      const k = e.key.toLowerCase();
      if (e.ctrlKey || e.metaKey) {
        if (k === "a") { e.preventDefault(); s.selectAll(); }
        else if (k === "c") { e.preventDefault(); s.copySelection(); }
        else if (k === "x") { e.preventDefault(); s.cutSelection(); }
        else if (k === "v") { e.preventDefault(); s.pasteAtPlayhead(); }
        else if (k === "d") { e.preventDefault(); s.duplicateSelection(); }
      } else if (!e.altKey && !e.shiftKey) {
        if (k === "c") { e.preventDefault(); s.splitSelectedOrUnderPlayhead(); }
        else if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault(); s.removeSelected();
        } else if (e.key === "Escape") {
          s.clearSelection();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="app-shell">
      <div className="topbar">
        <Link to="/"><button>← Proyectos</button></Link>
        <div className="brand">Editor</div>
        <button
          data-tour="transitions"
          onClick={() => openPanel("transitions")}
          style={{
            background: transitionsOpen ? "#22c55e" : "#4ade80", color: "#062818",
            borderColor: "#4ade80", fontWeight: 700,
            padding: "6px 14px",
            boxShadow: transitionsOpen ? "0 0 0 2px #4ade80" : undefined,
          }}
        >
          ✨ Transiciones
        </button>
        <button
          data-tour="effects"
          onClick={() => openPanel("effects")}
          style={{
            background: effectsOpen ? "#f97316" : "#fb923c", color: "#3d1f06",
            borderColor: "#fb923c", fontWeight: 700,
            padding: "6px 14px",
            boxShadow: effectsOpen ? "0 0 0 2px #fb923c" : undefined,
          }}
        >
          🎨 Efectos
        </button>
        <button
          data-tour="music"
          onClick={() => openPanel("music")}
          style={{
            background: musicOpen ? "#a855f7" : "#c084fc", color: "#1a0738",
            borderColor: "#c084fc", fontWeight: 700,
            padding: "6px 14px",
            boxShadow: musicOpen ? "0 0 0 2px #c084fc" : undefined,
          }}
        >
          🎵 Música
        </button>
        <div className="spacer" />
        <button
          onClick={() => setTourOpen(true)}
          title="Relanzar tour de bienvenida"
          style={{
            width: 32, height: 32, borderRadius: "50%",
            padding: 0, fontSize: 16, fontWeight: 700,
            background: "var(--accent)", color: "#08222e",
            borderColor: "var(--accent)",
          }}
        >?</button>
        <div className="user-chip">{dirty ? "Guardando…" : "Guardado ✓"}</div>
      </div>

      {tourOpen && <WelcomeTour onClose={() => setTourOpen(false)} />}

      {transitionsOpen && (
        <TransitionsPanel variant="sidebar" onClose={closeAll} />
      )}
      {effectsOpen && (
        <EffectsPanel onClose={closeAll} />
      )}
      {musicOpen && (
        <MusicPanel projectId={pid} library={library} onRefresh={refreshLibrary} onClose={closeAll} />
      )}

      <div className="editor-shell">
        <aside className="panel-library">
          <MediaLibrary projectId={pid} library={library} onRefresh={refreshLibrary} />
        </aside>

        <main className="panel-preview">
          <Preview library={library} />
        </main>

        <aside className="panel-inspector">
          <Inspector />
        </aside>

        <section className="panel-timeline">
          <Timeline mediaByClipId={mediaByClipId} />
        </section>
      </div>
    </div>
  );
}
